import { z, type ZodType } from '@customerio/hydra-validation';
import type { WarehouseSync as UIAPIWarehouseSync } from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';

/**
 * Warehouse-sync config (workspace-scoped singleton). At most one storage
 * destination is configured at a time; the integrations directory reads the
 * configured `type` and whether the sync is paused.
 */
export type WarehouseSync = UIAPIWarehouseSync;

// oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
const WarehouseSyncFromApi = z.looseObject({
  id: z.string(),
}) as unknown as ZodType<WarehouseSync, unknown>;

/** @public */
export const warehouseSync = defineEntity<WarehouseSync, WarehouseSync, void, undefined, 'workspace'>({
  name: 'warehouse_sync',
  scope: 'workspace',
  pk: () => 'current',
  schema: { read: WarehouseSyncFromApi },
});

export const warehouseSyncKeys = warehouseSync.keys;

/**
 * Config key → storage id, in precedence order (the first configured one wins).
 */
const STORAGE_CONFIGS: readonly [keyof UIAPIWarehouseSync, string][] = [
  ['azure_config', 'azure_blob'],
  ['gcs_config', 'gcs'],
  ['s3_config', 's3'],
  ['yandex_config', 'yandex'],
];

/** The configured storage id (`s3` / `gcs` / `azure_blob` / `yandex`), else `''`. */
export function warehouseSyncType(sync: WarehouseSync | null): string {
  if (sync == null) return '';
  const configured = STORAGE_CONFIGS.find(([configKey]) => sync[configKey] != null);
  return configured?.[1] ?? '';
}

const EXPORT_KEYS: readonly (keyof UIAPIWarehouseSync)[] = [
  'deliveries',
  'contents',
  'metrics',
  'subjects',
  'outputs',
  'people',
  'attributes',
  'campaigns',
  'broadcasts',
  'objects',
  'object_attributes',
  'events',
];

/**
 * A sync is "paused" when every export toggle is off. Schema v5+ also gates on
 * the `inbound` toggle, but only when the workspace has the Twilio action
 * enabled.
 */
export function warehouseSyncIsPaused(sync: WarehouseSync | null, twilioEnabled: boolean): boolean {
  if (sync == null) return false;
  const keys = [...EXPORT_KEYS];
  if (sync.version >= 5 && twilioEnabled) {
    keys.push('inbound');
  }
  return keys.every((key) => sync[key] === false);
}

export function parseWarehouseSync(raw: unknown): WarehouseSync {
  return warehouseSync.parse(raw);
}
